/* eslint-disable react-refresh/only-export-components */
import { Box, Flex, FormControl, Text, Avatar } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FormCustomLabel } from '../../components';
import { renderErrors } from '@/ui/util';
import EditorCustomVariables from '../FormEditor/EditorCustomVariables';
import { useField } from 'formik';
import { useLazyUsersQuery } from '@/store/query/account-invite';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { getInvalidVariableAlertConfig } from '@/utils/getInvalidVariableAlertConfig';
import { components } from 'react-select';
import { KText } from '@/ui-library/components';
import { filterSubjectEmailVariables } from '@/utils/filterEmailVariables';

const labelStyles = {
  fontSize: '14px',
  fontWeight: 'semiBold',
  minWidth: '50px',
  marginBottom: '0px',
};

const containerStyles = {
  paddingLeft: '16px',
  paddingRight: '16px',
  margin: '0px',
  borderColor: 'border-color',
};

const selectProps = {
  containerStyles: {
    width: '100%',
    borderBottom: '1px solid',
    ...containerStyles,
  },
  labelStyles,
  formControlContainerStyles: {
    flexDir: 'row',
    gap: '10px',
    alignItems: 'center',
  },
  newStyles: [
    {
      type: 'control',
      newStyles: {
        border: 'none',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
          cursor: 'pointer',
        },
      },
    },
  ],
};

const editorContainerStyles = {
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingBottom: '16px',
  margin: '0px',
  borderColor: 'border-color',
};

export const useEmailFieldWithCreate = ({
  formRef,
  templateData = undefined,
  bodyLabel = 'Body',
  showBodyLabel = true,
  showFromField = true,
  variables = undefined,
  DefaultallowedFromUsers = undefined,
  replaceContent = true,
  isBodyRequired = true,
  getEditorInstance = undefined,
  isReplyMode = false,
  disableConfig = undefined,
  showInvalidVariableAlert = true,
  pluginsConfig = [],
  wrapperProps = {},
  enableToAddress = false,
  containerPropStyles = {},
  editorHeight = 0,
  previewProps = {},
  toolBarProps = {},
}) => {
  const { body: bodyVal, subject: subjectVal } = templateData || {};
  const allUsers = useSelector(selectUsers);
  const [getUsers, { data: allowedFromUsers = [], isLoading: isUsersLoading }] =
    useLazyUsersQuery();
  const variablesListArr = Object.entries(variables || {}).flatMap(
    ([, values = []]) => values
  );

  // Filter out sender.email_signature from variables
  const filteredVariables = filterSubjectEmailVariables(variables);

  useEffect(() => {
    if (showFromField && !DefaultallowedFromUsers) {
      getUsers({
        params: {
          scope: 'email_delegatable',
        },
      });
    }
  }, [getUsers, showFromField]); // eslint-disable-line react-hooks/exhaustive-deps

  const [emailFields, setEmailFields] = useState({
    isCc: false,
    isBcc: false,
  });

  const { isBcc, isCc } = emailFields;

  const {
    // from: fromVal,
    cc: ccVal = [],
    bcc: bccVal = [],
    body,
    subject,
  } = formRef?.current?.values || {};

  const editorMinHeight = editorHeight + 'px' || '350px';
  const editorHeightUpdated = editorHeight - 80 + 'px' || '300px';

  useEffect(() => {
    if (emailFields?.isCc && emailFields?.isBcc) return;
    if (formRef?.current?.values?.cc?.length) {
      setEmailFields((prev) => ({ ...prev, isCc: true }));
    }
    if (formRef?.current?.values?.bcc?.length) {
      setEmailFields((prev) => ({ ...prev, isBcc: true }));
    }
  }, [ccVal, bccVal]); // eslint-disable-line react-hooks/exhaustive-deps

  const initialFields: Array<Record<string, unknown>> = [
    {
      name: 'from',
      type: 'select',
      label: 'From',
      showErrorStyles: true,
      showMandatoryIcon: true,
      isMulti: false,
      valueKey: 'id',
      isLoading: isUsersLoading,
      options: DefaultallowedFromUsers || allowedFromUsers,
      customFormatLabel: CustomLabel,
      components: { Option: CustomSelectOption },
      showAfterContent: true,
      AfterContentsComp: () => {
        return (
          <Flex gap="0.5em" position="absolute" right="65px" top="14px">
            {!isCc && (
              <Text
                size="sm-medium"
                color="ats-primary"
                cursor="pointer"
                onClick={() =>
                  setEmailFields((prev) => ({ ...prev, isCc: true }))
                }
              >
                CC
              </Text>
            )}
            {!isBcc && (
              <Text
                size="sm-medium"
                color="ats-primary"
                cursor="pointer"
                onClick={() =>
                  setEmailFields((prev) => ({ ...prev, isBcc: true }))
                }
              >
                BCC
              </Text>
            )}
          </Flex>
        );
      },
      // isRequired: true,
      // validate: (value, item) => {
      //   return value || item?.isRequired === false
      //     ? undefined
      //     : `${item?.label} is required`;
      // },
      ...selectProps,
    },
  ];

  if (enableToAddress) {
    initialFields.push({
      // change name once BE is done
      name: 'toAddress',
      type: 'selectWithCreateV1',
      label: 'To',
      isMulti: true,
      showMandatoryIcon: true,
      createPlaceHolder: '',
      placeholder: 'Enter an email address',
      // isRequired: true,
      isAppendToBody: true,
      customFormatLabel: (data: Record<string, unknown> = {}) => {
        const label =
          typeof data.label === 'string'
            ? data.label
            : String(data.label ?? '');
        const name =
          typeof data.name === 'string' ? data.name : String(data.name ?? '');
        const avatar =
          typeof data.avatar === 'string' ? data.avatar : undefined;
        return (
          <Flex gap="6px" alignItems={'center'}>
            <Avatar
              src={avatar}
              name={label}
              width="16px"
              height="16px"
              borderRadius="6px"
              className="avatar-xs"
            />
            <KText>{`${name} ${label}`}</KText>
          </Flex>
        );
      },
      options: allUsers?.map((user) => ({
        label: user?.email,
        value: user?.id,
        ...user,
      })),
      customNoOptionsMessage: (props) => {
        const searchTerm = props?.selectProps?.inputValue;
        const searchTermLen = searchTerm.length;
        return (
          <components.NoOptionsMessage {...props}>
            <Box
              fontSize="14px"
              borderColor="border-color"
              color="secondary-700"
              textAlign="left"
            >
              {searchTermLen < 2
                ? 'Please type 2 or more letters'
                : 'Enter valid email to add as new option'}
            </Box>
          </components.NoOptionsMessage>
        );
      },
      validateNewOption: (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      },
      ...selectProps,
    });
  }

  const midFields: Array<Record<string, unknown>> = [];

  const lastFields = [
    !isReplyMode && {
      name: 'subject',
      label: 'Subject',
      type: 'variableInput',
      maxLength: 255,
      showErrorStyles: true,
      showMandatoryIcon: true,
      disableHoverBg: true,
      content: subjectVal,
      // isRequired: true,
      // validate: (value, item) => {
      //   return value || item?.isRequired === false
      //     ? undefined
      //     : `${item?.label} is required`;
      // },
      containerStyles: {
        width: '100%',
        borderTopRadius: !showFromField && '6px',
        border: !showFromField && '1px solid',
        borderBottom: '1px solid',
        ...containerStyles,
      },
      labelStyles,
      style: { border: 'none', _hover: {}, _focus: {} },
      formControlContainerStyles: {
        flexDir: 'row',
        gap: '10px',
        alignItems: 'center',
      },
      formFieldContainerStyles: {
        overflow: 'hidden',
      },
      editorContainerStyles: {
        border: 'none',
        _hover: {},
        _focus: {},
      },
      enableCustomVariables: true,
      customVariableList: filteredVariables,
      CustomRightElement: () => {
        const [, { value = '' }, { setValue }] = useField(`subject`);
        const customOnClick = (val) => {
          const newVal = value + ' ' + val;
          setValue(newVal);
        };

        if (!variables) return null;

        return (
          <Box mt={'0px'}>
            <EditorCustomVariables
              config={variables}
              customOnClick={customOnClick}
              wrapperProps={{ pt: '0px' }}
            />
          </Box>
        );
      },
    },
    {
      name: 'body',
      type: 'editor',
      pluginsConfig: pluginsConfig,
      enableCustomVariables: true,
      content: bodyVal,
      customVariableList: variables,
      disableConfig,
      containerStyles: {
        width: '100%',
        border: showFromField ? 'none' : '1px solid',
        borderBottomRadius: showFromField ? 'none' : '6px',
        borderTop: 'none',
        ...editorContainerStyles,
        ...containerPropStyles,
      },
      labelStyles,
      editorContainerStyles: {
        border: 'none',
        minHeight: editorMinHeight,
      },
      getEditorInstance,
      backgroundColor: 'red',
      editorWraperStyles: {
        padding: '0px',
        paddingTop: '10px',
        minHeight: editorHeight ? `${editorHeight - 50}px` : '100%',
        height: '100%',
      },
      editorStyles: { padding: '0px', height: editorHeightUpdated },
      toolbarWrapperStyles: { marginLeft: '-10px' },
      replaceContent,
      previewProps,
      toolBarProps,
    },
  ];

  const bodyLabelConfig = {
    type: 'custom',
    Field: () => {
      const { submitCount } = formRef?.current || {};

      // Determine if subject or body are missing based on submit count
      const subjectError = !subject && submitCount > 0;
      const bodyError = isBodyRequired && !body && submitCount > 0;

      const errorMessage =
        subjectError && bodyError
          ? 'Subject and Email content are required'
          : subjectError
            ? 'Subject is required'
            : bodyError
              ? 'Email content is required'
              : '';

      return (
        <FormControl>
          {showBodyLabel && <FormCustomLabel item={{ label: bodyLabel }} />}
          {/* Show error message if applicable */}
          {errorMessage && <Box>{renderErrors('', errorMessage)}</Box>}
        </FormControl>
      );
    },
  };

  const invalidVariableAlertConfig = getInvalidVariableAlertConfig({
    variablesListArr,
    isBodySubject: true,
    wrapperProps,
  });

  if (emailFields?.isCc || ccVal?.length) {
    midFields.push({
      id: 'cc',
      name: 'cc',
      label: 'CC',
      type: 'selectWithCreateV1',
      createPlaceHolder: '',
      placeholder: 'Enter an email address',
      isMulti: true,
      isRequired: false,
      showErrorStyles: true,
      showMandatoryIcon: false,
      isAppendToBody: true,
      customFormatLabel: (data: Record<string, unknown> = {}) => {
        const label =
          typeof data.label === 'string'
            ? data.label
            : String(data.label ?? '');
        const name =
          typeof data.name === 'string' ? data.name : String(data.name ?? '');
        const avatar =
          typeof data.avatar === 'string' ? data.avatar : undefined;
        return (
          <Flex gap="6px" alignItems={'center'}>
            <Avatar
              src={avatar}
              name={label}
              width="16px"
              height="16px"
              borderRadius="6px"
              className="avatar-xs"
            />
            <KText>{`${name} ${label}`}</KText>
          </Flex>
        );
      },
      options: allUsers?.map((user) => ({
        label: user?.email,
        value: user?.id,
        avatar: user?.avatar,
        ...user,
      })),
      customNoOptionsMessage: (props) => {
        const searchTerm = props?.selectProps?.inputValue;
        const searchTermLen = searchTerm.length;
        return (
          <components.NoOptionsMessage {...props}>
            <Box
              fontSize="14px"
              borderColor="border-color"
              color="secondary-700"
              textAlign="left"
            >
              {searchTermLen < 2
                ? 'Please type 2 or more letters'
                : 'Enter valid email to add as new option'}
            </Box>
          </components.NoOptionsMessage>
        );
      },
      validateNewOption: (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      },
      ...selectProps,
    });
  }

  if (emailFields?.isBcc || bccVal?.length) {
    midFields.push({
      id: 'bcc',
      name: 'bcc',
      label: 'BCC',
      type: 'selectWithCreateV1',
      createPlaceHolder: '',
      placeholder: 'Enter an email address',
      isRequired: false,
      isMulti: true,
      showErrorStyles: true,
      isAppendToBody: true,
      customFormatLabel: (data: Record<string, unknown> = {}) => {
        const label =
          typeof data.label === 'string'
            ? data.label
            : String(data.label ?? '');
        const name =
          typeof data.name === 'string' ? data.name : String(data.name ?? '');
        const avatar =
          typeof data.avatar === 'string' ? data.avatar : undefined;
        return (
          <Flex gap="6px" alignItems={'center'}>
            <Avatar
              src={avatar}
              name={label}
              width="16px"
              height="16px"
              borderRadius="6px"
              className="avatar-xs"
            />
            <KText>{`${name} (${label})`}</KText>
          </Flex>
        );
      },
      options: allUsers?.map((user) => ({
        label: user?.email,
        value: user?.id,
        avatar: user?.avatar,
        ...user,
      })),
      customNoOptionsMessage: (props) => {
        const searchTerm = props?.selectProps?.inputValue;
        const searchTermLen = searchTerm.length;
        return (
          <components.NoOptionsMessage {...props}>
            <Box
              fontSize="14px"
              borderColor="border-color"
              color="secondary-700"
              textAlign="left"
            >
              {searchTermLen < 2
                ? 'Please type 2 or more letters'
                : 'Enter valid email to add as new option'}
            </Box>
          </components.NoOptionsMessage>
        );
      },
      validateNewOption: (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      },
      ...selectProps,
    });
  }

  // Combine all fields and return
  const fieldConfig: Array<Record<string, unknown>> = [];
  if (showFromField) {
    fieldConfig.push(...initialFields);
  } else if (bodyLabel) {
    fieldConfig.push(bodyLabelConfig);
  }
  fieldConfig.push(...midFields, ...lastFields);
  if (variables && showInvalidVariableAlert)
    fieldConfig.push(invalidVariableAlertConfig);
  return fieldConfig;
};

const CustomSelectOption = ({ innerProps, data }) => {
  const { name, email, avatar, isPrimaryEmail } = data || {};

  return (
    <Flex
      {...innerProps}
      padding="10px 5px"
      gap="5px"
      alignItems="center"
      _hover={{
        bg: '#F4F4F5',
        borderRadius: '5px',
        cursor: 'pointer',
      }}
    >
      <Avatar
        h="20px"
        w="20px"
        borderRadius="6px"
        size="xs"
        name={name}
        src={avatar}
      />
      <Text fontSize="sm">
        {name}
        {email && ` (${email})`}
      </Text>
      {isPrimaryEmail && (
        <Text
          size="sm-medium"
          bgColor="ats-tertiary"
          color="ats-primary"
          p="4px 8px"
          m="4px"
          borderRadius="sm"
        >
          Primary
        </Text>
      )}
    </Flex>
  );
};

const CustomLabel = (option) => {
  const { name, email, avatar, isPrimaryEmail } = option;

  return (
    <Flex gap="5px" alignItems="center">
      <Avatar
        h="20px"
        w="20px"
        borderRadius="6px"
        size="xs"
        name={name}
        src={avatar}
      />
      <Text fontSize="sm">
        {name}
        {email && ` (${email})`}
      </Text>
      {isPrimaryEmail && (
        <Text
          size="sm-medium"
          bgColor="ats-tertiary"
          color="ats-primary"
          p="4px 8px"
          m="4px"
          borderRadius="sm"
        >
          Primary
        </Text>
      )}
    </Flex>
  );
};
